import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ProductCard from '../../components/ProductCard';
import MagneticButton from '../../components/MagneticButton';
import { Reveal, RevealGroup, RevealItem } from '../../components/Reveal';
import '../css/ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeAccordion, setActiveAccordion] = useState('description');

  const product = {
    id: id,
    name: 'Professional Angled Paint Brush - 2.5 inch',
    sku: 'PAB-25-001',
    rating: 5,
    reviews: 89,
    description: 'Precision-engineered professional-grade angled brush featuring high-density synthetic bristles optimised for smooth paint application. Ergonomically designed handle provides exceptional comfort and control during extended use. Ideal for cutting-in, trim work, and premium coating applications.',
    features: [
      'High-quality bristles crafted for durability',
      'Ergonomically superior grip for easy handling',
      'Seamless blend from brush to handle',
      'Specially designed for premium coating'
    ],
    images: [
      '/images/product-details/product-detail-1.png',
      '/images/product-details/product-detail-2.png',
      '/images/product-details/product-detail-3.png',
      '/images/product-details/product-detail-4.png',
      '/images/product-details/product-detail-5.png'
    ],
    specifications: [
      { label: 'Material', value: 'High-density synthetic bristles, polypropylene handle, stainless steel ferrule' },
      { label: 'Brush Width', value: '2.5 inches (63.5mm)' },
      { label: 'Overall Length', value: '9 inches (229mm)' },
      { label: 'Bristle Length', value: '2.25 inches (57mm)' },
      { label: 'Weight', value: '3.2 oz (90g)' },
      { label: 'Finish', value: 'Matte ergonomic grip with hanging loop' },
      { label: 'Packaging Options', value: 'Individual sleeve, 6-pack display box, 24-pack master carton' },
      { label: 'Recommended Use', value: 'Water-based or solvent-based paints, varnish, or stain-coat' }
    ],
    volumePricing: [
      { quantity: '1-100 units', price: '$12.99', totalValue: '$12.99 - $1,299.99' },
      { quantity: '101-500 units', price: '$10.75', totalValue: '$1,086.75 - $5,375.00' },
      { quantity: '501-1,000 units', price: '$9.50', totalValue: '$4,759.50 - $9,500.00' },
      { quantity: '1,001-5,000 units', price: '$8.25', totalValue: '$8,258.25 - $41,250.00' },
      { quantity: '5,000+ units', price: 'Contact for pricing', totalValue: 'Custom quotation available' }
    ]
  };

  const relatedProducts = [
    { id: 2, name: 'Paint Brush', description: 'Professional Flat Paint Brush - 3 inch', image: '/images/product-details/product-detail-2.png' },
    { id: 3, name: 'Paint Rollers', description: 'Premium Microfiber Roller - 9 inch', image: '/images/product-details/product-detail-3.png' },
    { id: 4, name: 'Paint System', description: 'Grid Fine System Set - 5 Piece', image: '/images/product-details/product-detail-4.png' },
    { id: 5, name: 'Accessories', description: 'Multi-Functional Paint Tray Kit', image: '/images/product-details/product-detail-5.png' }
  ];

  const toggleAccordion = (section) => {
    setActiveAccordion(activeAccordion === section ? '' : section);
  };

  return (
    <div className="product-details">
      <div className="container">
        <div className="product-hero">
          <Reveal as="div" className="product-gallery">
            <div className="main-image">
              <img src={product.images[selectedImage]} alt={product.name} />
            </div>
            <div className="thumbnail-gallery">
              {product.images.map((image, index) => (
                <div
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={image} alt={`${product.name} ${index + 1}`} />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal as="div" className="product-info" delay={0.1}>
            <h1>{product.name}</h1>
            <div className="product-meta">
              <span className="sku">SKU: {product.sku}</span>
              <div className="rating">
                {[...Array(5)].map((_, index) => (
                  index < product.rating ? <StarIcon key={index} /> : <StarBorderIcon key={index} />
                ))}
                <span>({product.reviews})</span>
              </div>
            </div>
            <p className="description">{product.description}</p>
            <ul className="features-list">
              {product.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal as="div" className="product-specifications-section">
          <h2 className="section-title">Product Specifications</h2>
          <table className="specifications-table">
            <thead>
              <tr>
                <th>Specification</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {product.specifications.map((spec, index) => (
                <tr key={index}>
                  <td>{spec.label}</td>
                  <td>{spec.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <div className="product-details-section">
          <h2 className="section-title">Product Details</h2>

          <div className="accordion">
            <div className="accordion-item">
              <button
                className={`accordion-header ${activeAccordion === 'description' ? 'active' : ''}`}
                onClick={() => toggleAccordion('description')}
              >
                Detailed Description
                <span className="accordion-icon">{activeAccordion === 'description' ? <RemoveIcon /> : <AddIcon />}</span>
              </button>
              {activeAccordion === 'description' && (
                <div className="accordion-content">
                  <p>The Professional Angled Paint Brush is an engineers first choice for achieving a professional-quality edge precision that works and quality are second-to-none.</p>
                  <p>The high-density synthetic bristles come after offering superior paint pick-up and release compared to natural bristles, making it ideal for use with all water-based paint types (enamels, acrylics, etc.).</p>
                  <p>Professional synthetic angled paint brush comprises controlled bristle properties that go beyond strength and create non-frayed edges, which in turn creates controlled painting accuracy. The famed angled bristle cut provides exceptional maneuverability and precise edge-cutting when painting corners, trim, and window casings. Our brush is crafted using a unique ferrule design that keeps the bristles secure and eliminates shedding during application.</p>
                  <p>The contoured, ergonomic handle is made of durable polypropylene (PP) and features a comfortable, non-slip grip so users won't experience hand fatiguing even during extended painting sessions.</p>
                  <p>This durable paint brush is made additionally from recyclable materials that form the core of its specification, making it an excellent choice for builders and operating tradespeople.</p>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button
                className={`accordion-header ${activeAccordion === 'technical' ? 'active' : ''}`}
                onClick={() => toggleAccordion('technical')}
              >
                Technical Specifications
                <span className="accordion-icon">{activeAccordion === 'technical' ? <RemoveIcon /> : <AddIcon />}</span>
              </button>
              {activeAccordion === 'technical' && (
                <div className="accordion-content">
                  <p>Technical specifications content here...</p>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button
                className={`accordion-header ${activeAccordion === 'materials' ? 'active' : ''}`}
                onClick={() => toggleAccordion('materials')}
              >
                Materials & Quality
                <span className="accordion-icon">{activeAccordion === 'materials' ? <RemoveIcon /> : <AddIcon />}</span>
              </button>
              {activeAccordion === 'materials' && (
                <div className="accordion-content">
                  <p>Materials and quality information here...</p>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button
                className={`accordion-header ${activeAccordion === 'packaging' ? 'active' : ''}`}
                onClick={() => toggleAccordion('packaging')}
              >
                Export Packaging
                <span className="accordion-icon">{activeAccordion === 'packaging' ? <RemoveIcon /> : <AddIcon />}</span>
              </button>
              {activeAccordion === 'packaging' && (
                <div className="accordion-content">
                  <p>Export packaging details here...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <Reveal as="div" className="volume-pricing-section">
          <h2 className="section-title">Volume Pricing & Quotation</h2>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>Order Quantity</th>
                <th>Unit Price</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {product.volumePricing.map((pricing, index) => (
                <tr key={index}>
                  <td>{pricing.quantity}</td>
                  <td>{pricing.price}</td>
                  <td>{pricing.totalValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pricing-note">
            <p>Volume discounts are available. Contact our export team for pricing on bulk orders over 5,000 units. All customization options (branding & colors) are available.</p>
          </div>
          <div className="action-buttons">
            <MagneticButton to="/contact" className="btn-primary">Request Custom Quotation</MagneticButton>
            <button className="btn-ghost">
              <DownloadOutlinedIcon />
              Download Specification Sheet
            </button>
          </div>
        </Reveal>

        <div className="related-products-section">
          <h2 className="section-title">Related Products</h2>
          <RevealGroup className="related-grid">
            {relatedProducts.map(relatedProduct => (
              <RevealItem key={relatedProduct.id}>
                <ProductCard
                  to={`/product/${relatedProduct.id}`}
                  image={relatedProduct.image}
                  title={relatedProduct.name}
                  description={relatedProduct.description}
                  cta="View Details"
                />
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </div>

      <Reveal as="div" className="bottom-sections">
        <div className="container">
          <div className="bottom-grid">
            <div className="download-resources">
              <h3>Download Resources</h3>
              <ul>
                <li>
                  <DescriptionOutlinedIcon />
                  <span>Product Specification Sheet</span>
                </li>
                <li>
                  <DescriptionOutlinedIcon />
                  <span>Product Catalogs & Brochure</span>
                </li>
                <li>
                  <DescriptionOutlinedIcon />
                  <span>Safety Data Sheet (SDS)</span>
                </li>
              </ul>
            </div>

            <div className="certifications">
              <h3>Certifications & Compliance</h3>
              <ul>
                <li>
                  <CheckCircleIcon />
                  <span>Quality Certified</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>ISO 9001:2015 (Quality Management)</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>ISO 14001:2015 (Environmental Management)</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>BSCI Compliance</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>CE & RoHS Certification</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>REACH Product Safety Standards</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>FDA Regulatory Compliance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
};

export default ProductDetails;
